import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { typography, colors } from "../styles/designSystem";
import { SectionHeadline } from "./Typography";

/**
 * Employment record.
 *
 * Titles, companies, and dates mirror the LinkedIn profile exactly so a recruiter
 * cross-checking the two never hits a discrepancy. Each entry is rendered as a
 * selectable terminal record rather than a card: company, leader dots, date span,
 * with the role and a software-first summary underneath. Entries without a `to`
 * have no case study yet and render as static rows: no link, no hover, no CTA.
 */
const records = [
    {
        to: "/portfolio/cartsense",
        company: "CartSense",
        title: "Founder & Software Engineer",
        date: "Dec 2025 — Present",
        status: "ACTIVE",
        summary:
            "AI grocery-commerce platform built solo. A multi-stage LLM pipeline — moderation, dietary-conflict detection, streaming generation, structured outputs, post-generation validation — feeds a Kroger OAuth 2.0 commerce integration with relevance-scored product matching across 15+ store brands.",
        stack: ["Next.js", "TypeScript", "Firebase", "OpenAI", "Stripe"],
    },
    {
        to: "/work/att",
        company: "AT&T",
        title: "Senior Product Designer",
        date: "Dec 2025 — Present",
        status: "ACTIVE",
        summary:
            "AI design-to-application platform that turns natural-language prompts into interactive product experiences. Figma MCP integrations compose from approved components; slash commands, AI skills, and hard-rule guardrails constrain what can be generated, and validation workflows check design-system compliance before anything deploys.",
        stack: ["React", "TypeScript", "Figma MCP", "AI Guardrails"],
    },
    {
        to: "/work/NsaBrands",
        company: "NSA Brands",
        title: "Senior Software Engineer — AI & Mobile",
        date: "Jun 2024 — Jul 2026",
        summary:
            "End-to-end mobile rental workflow — reserve a unit, complete the agreements, and gain access in-app — plus a self-service move-out flow where a multi-stage vision pipeline compares a live camera capture against prior unit-condition and manager-cleaning images to decide whether the unit passes.",
        stack: ["React Native", "Node.js", "Multimodal AI", "Computer Vision"],
    },
    {
        to: "/work/spectrum",
        company: "Spectrum",
        title: "Product Engineer",
        date: "Jun 2021 — Jul 2025",
        summary:
            "Built WIN, a personalized agent-assist platform surfacing customer history, account data, and troubleshooting context for support agents nationwide. Average call handling fell from roughly five minutes to three; the platform won increased year-over-year funding and expanded into further support workflows.",
        stack: ["React", "Design Systems", "Accessibility"],
    },
    {
        to: "/portfolio/nebo",
        company: "Project Nebo",
        title: "Founder & Software Engineer",
        date: "Jun 2020 — Present",
        status: "ACTIVE",
        summary:
            "Asset registration and recovery platform owned end to end. A React Native client over Supabase and PostgreSQL, with row-level security enforcing per-user isolation and ownership verification, plus a recovery engine that scores marketplace listing photos against registered assets through multi-stage validation.",
        stack: ["React Native", "Supabase", "PostgreSQL", "Claude Vision"],
    },
    {
        to: "/work/tilt",
        company: "tilt by Smarterhome",
        title: "Software Engineer — Mobile & IoT",
        date: "Feb 2019 — Sep 2020",
        summary:
            "Production React Native features for iOS and Android apps behind connected-home and IoT products: device onboarding, automation, account management, and IoT integrations across mobile and web, plus the reusable application patterns and component libraries the team built on.",
        stack: ["React Native", "iOS", "Android", "IoT"],
    },
    {
        to: "/work/Verizon",
        company: "Verizon",
        title: "Software Engineer — Design Systems",
        date: "Jun 2018 — Jun 2021",
        summary:
            "Built the reusable component libraries, accessibility standards, and scalable UI patterns behind Verizon's enterprise design system, adopted across consumer and business product teams.",
        stack: ["Design Systems", "Component Libraries", "A11y"],
    },
    {
        to: "/work/Caterpillar",
        company: "Caterpillar",
        title: "Software Engineer — Frontend & Mobile",
        date: "Oct 2015 — Apr 2018",
        summary:
            "Owned end-to-end development of CAT Mobile applications for equipment maintenance, diagnostics, and dealer service workflows, building responsive web and mobile experiences in React and JavaScript.",
        stack: ["React", "JavaScript", "Mobile"],
    },
];

const Section = styled.section`
    padding: 4rem 1.5rem;
    max-width: 1100px;
    margin: 0 auto;
    text-align: center;
`;

const Intro = styled.p`
    font-size: ${typography.fontSize[300]};
    line-height: 1.8;
    color: ${({ theme }) => theme.subtleText};
    max-width: 640px;
    margin: 0 auto 3rem;
`;

const RecordList = styled.div`
    display: flex;
    flex-direction: column;
    border-top: 1px solid ${({ theme }) => theme.borderLight};
    text-align: left;
`;

const Entry = styled(Link)`
    display: block;
    padding: 1.5rem 1rem 1.5rem 2rem;
    position: relative;
    border-bottom: 1px solid ${({ theme }) => theme.borderLight};
    color: ${({ theme }) => theme.text};
    transition: background 0.14s ease-out;

    /* Selection marker sits in reserved gutter space so hover never shifts the row. */
    &::before {
        content: "\\25B8";
        position: absolute;
        left: 0.6rem;
        top: 1.55rem;
        color: ${({ theme }) => theme.primary};
        opacity: 0.35;
        transition: opacity 0.14s ease-out;
    }

    ${({ $static, theme }) =>
        !$static &&
        `
        &:hover {
            background: ${theme.surfaceHover};
            color: ${theme.text};

            &::before {
                opacity: 1;
            }
        }
    `}

    @media (max-width: 640px) {
        padding: 1.25rem 0.5rem 1.25rem 1.6rem;
    }
`;

// The record's top line: company, leader dots, date — a terminal index row.
const IndexRow = styled.div`
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
    margin-bottom: 0.4rem;
`;

const Company = styled.span`
    font-family: ${typography.fontFamily.display};
    font-size: 1.15rem;
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ theme }) => theme.primary};
    text-shadow: 0 0 8px ${({ theme }) => theme.glow};
    white-space: nowrap;

    @media (max-width: 640px) {
        font-size: 0.95rem;
        white-space: normal;
    }
`;

// The dotted run between company and date. Collapses on small screens, where the
// date wraps to its own line and leader dots would just be noise.
const Leader = styled.span`
    flex: 1;
    border-bottom: 1px dotted ${({ theme }) => theme.borderMedium};
    transform: translateY(-0.25rem);

    @media (max-width: 640px) {
        display: none;
    }
`;

const DateSpan = styled.span`
    font-family: ${typography.fontFamily.display};
    font-size: 0.8rem;
    letter-spacing: ${typography.letterSpacing.wider};
    text-transform: uppercase;
    color: ${({ theme }) => theme.subtleText};
    white-space: nowrap;

    @media (max-width: 640px) {
        font-size: 0.68rem;
        letter-spacing: 0.08em;
    }
`;

const StatusTag = styled.span`
    font-family: ${typography.fontFamily.display};
    font-size: 0.6rem;
    letter-spacing: ${typography.letterSpacing.widest};
    color: ${({ theme }) => theme.background};
    background: ${colors.emerald[500]};
    padding: 0.1rem 0.4rem;
    white-space: nowrap;
`;

const Title = styled.div`
    font-family: ${typography.fontFamily.display};
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ theme }) => theme.text};
    margin-bottom: 0.6rem;
`;

const Summary = styled.p`
    font-size: ${typography.fontSize[200]};
    line-height: 1.75;
    color: ${({ theme }) => theme.subtleText};
    margin: 0 0 0.85rem;
    max-width: 78ch;
`;

const StackRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
`;

const Chip = styled.span`
    font-family: ${typography.fontFamily.display};
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ theme }) => theme.subtleText};
    border: 1px solid ${({ theme }) => theme.borderLight};
    padding: 0.2rem 0.5rem;
`;

// The row's call to action. It inverts on row hover the same way the terminal
// buttons do — phosphor fill, screen-colored text — so the whole record reads as one
// selectable entry rather than a paragraph with a link in it.
const Access = styled.span`
    display: inline-block;
    font-family: ${typography.fontFamily.display};
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wider};
    color: ${({ theme }) => theme.primary};
    margin-left: auto;
    /* Padding is always applied so igniting on hover never reflows the row. The
       right side is trimmed by the tracking that trails the final character. */
    padding: 0.25rem calc(0.5rem - ${typography.letterSpacing.wider}) 0.25rem 0.5rem;
    border: 1px solid transparent;
    transition: background 0.14s ease-out, color 0.14s ease-out,
                border-color 0.14s ease-out, box-shadow 0.14s ease-out;

    &::before {
        content: "[";
        opacity: 0.5;
        margin-right: 0.3rem;
    }

    &::after {
        content: "]";
        opacity: 0.5;
        margin-left: 0.3rem;
    }

    ${Entry}:hover & {
        background: ${({ theme }) => theme.primary};
        border-color: ${({ theme }) => theme.primary};
        color: ${({ theme }) => theme.background};
        text-shadow: none;
        box-shadow: 0 0 14px ${({ theme }) => theme.glow};
    }

    @media (max-width: 640px) {
        margin-left: 0;
    }
`;

const WorkHistorySection = () => (
    <Section>
        <SectionHeadline>Employment Record</SectionHeadline>
        <Intro>
            Ten years building software across enterprise product teams and as a solo founder.
            Titles and dates below match my LinkedIn record.
        </Intro>

        <RecordList>
            {records.map((record) => (
                <Entry
                    key={record.company}
                    as={record.to ? Link : "div"}
                    to={record.to}
                    $static={!record.to}
                >
                    <IndexRow>
                        <Company>{record.company}</Company>
                        {record.status && <StatusTag>{record.status}</StatusTag>}
                        <Leader aria-hidden="true" />
                        <DateSpan>{record.date}</DateSpan>
                    </IndexRow>
                    <Title>{record.title}</Title>
                    <Summary>{record.summary}</Summary>
                    <StackRow>
                        {record.stack.map((tech) => (
                            <Chip key={tech}>{tech}</Chip>
                        ))}
                        {record.to && <Access>Access Record</Access>}
                    </StackRow>
                </Entry>
            ))}
        </RecordList>
    </Section>
);

export default WorkHistorySection;
